/**
 * Renders an interactive lesson — the Learnica equivalent of a TalentCraft unit.
 *
 * Block types in the imported content: text, image, flashcard, process, tabs
 * (tabs / accordion), question, labeled_graphic, multimedia, continue.
 * Anything unrecognised renders as nothing rather than breaking the lesson.
 */
import { useState } from 'react';
import type { CraftBlock } from '../api';
import { RichText, plainText } from './RichText';

type Theme = { primaryColor?: string; primaryFont?: string; spacing?: string } | null;

export function CraftLesson({ blocks, theme, downloadDisabled = false }: { blocks: CraftBlock[]; theme?: Theme; downloadDisabled?: boolean }) {
  const style = { '--craft-accent': theme?.primaryColor ?? '#1b68b3' } as React.CSSProperties;
  return (
    <div className="craft" style={style} data-spacing={theme?.spacing ?? 'normal'}>
      {blocks.map((b, i) => <Block key={b.id ?? i} block={b} downloadDisabled={downloadDisabled} />)}
    </div>
  );
}

function Block({ block, downloadDisabled }: { block: CraftBlock; downloadDisabled: boolean }) {
  switch (block.type) {
    case 'text': return <TextBlock block={block} />;
    case 'image': return <ImageBlock block={block} />;
    case 'flashcard': return <FlashcardBlock block={block} />;
    case 'process': return <ProcessBlock block={block} />;
    case 'tabs': return <TabsBlock block={block} />;
    case 'question': return <QuestionBlock block={block} />;
    case 'labeled_graphic': return <LabeledGraphic block={block} />;
    case 'multimedia': return <MultimediaBlock block={block} downloadDisabled={downloadDisabled} />;
    case 'continue': return <div className="craft-divider" />;
    default: return null;
  }
}

/* ───────────────────────────── text ───────────────────────────── */

function TextBlock({ block }: { block: CraftBlock }) {
  const v = block.variant ?? 'paragraph';
  const headings: any[] = block.headings ?? [];
  const texts: any[] = block.texts ?? [];
  const quote = v === 'quote';
  const motivation = v.startsWith('motivation');

  return (
    <section className={`craft-text craft-text--${v}`}>
      {headings.map((h, i) => (
        <RichText key={i} value={h} className={v.includes('subheading') ? 'craft-subheading' : 'craft-heading'} />
      ))}
      {quote
        ? texts.map((t, i) => <blockquote key={i}><RichText value={t} /></blockquote>)
        : texts.map((t, i) => (
            <RichText key={i} value={t} className={motivation ? 'craft-callout' : 'craft-body'} />
          ))}
    </section>
  );
}

/* ───────────────────────────── images ───────────────────────────── */

/**
 * Where an image is. Most blocks hold the image itself; a tab or an accordion
 * item holds it one level down, as `media: { type: 'image', image: {...} }` —
 * which, read as an image, has no address, so those pictures were left out
 * (found in the rehearsal on the real lessons).
 */
const imgSrc = (img: any): string => {
  const i = img?.type === 'image' && img?.image ? img.image : img;
  return i?.urls?.original ?? i?.urls?.large ?? i?.url ?? '';
};

function ImageBlock({ block }: { block: CraftBlock }) {
  const images: any[] = block.images ?? [];
  const v = block.variant ?? 'centered';

  if (v === 'carousel') return <Carousel images={images} />;
  if (v === 'text-on-image') {
    const img = images[0];
    return (
      <section className="craft-hero" style={{ backgroundImage: `url(${imgSrc(img)})` }}>
        <div className="craft-hero__scrim">
          {(block.headings ?? []).map((h: any, i: number) => <RichText key={i} value={h} className="craft-heading" />)}
          {(block.texts ?? []).map((t: any, i: number) => <RichText key={i} value={t} />)}
        </div>
      </section>
    );
  }

  return (
    <figure className={`craft-image craft-image--${v}`}>
      <div className={v === 'four-column-grid' ? 'craft-grid craft-grid--4' : ''}>
        {images.map((img, i) => (
          <img key={img.id ?? i} src={imgSrc(img)} alt={plainText(img.alt) || ''} loading="lazy" />
        ))}
      </div>
      {block.caption ? <figcaption><RichText value={block.caption} /></figcaption> : null}
    </figure>
  );
}

function Carousel({ images }: { images: any[] }) {
  const [at, setAt] = useState(0);
  if (!images.length) return null;
  const go = (d: number) => setAt((n) => (n + d + images.length) % images.length);
  return (
    <div className="craft-carousel">
      <img src={imgSrc(images[at])} alt="" loading="lazy" />
      {images.length > 1 && (
        <div className="craft-carousel__bar">
          <button type="button" onClick={() => go(-1)} aria-label="Previous image">‹</button>
          <span>{at + 1} / {images.length}</span>
          <button type="button" onClick={() => go(1)} aria-label="Next image">›</button>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── flashcards ───────────────────────────── */

function FlashcardBlock({ block }: { block: CraftBlock }) {
  const cards: any[] = block.flashcards ?? [];
  return (
    <div className={`craft-cards craft-cards--${block.variant ?? 'grid'}`}>
      {cards.map((c, i) => <Flashcard key={c.id ?? i} card={c} />)}
    </div>
  );
}

function Flashcard({ card }: { card: any }) {
  const [flipped, setFlipped] = useState(false);
  const face = flipped ? card.faces?.back : card.faces?.front;
  return (
    <button type="button" className={`craft-card ${flipped ? 'is-flipped' : ''}`} onClick={() => setFlipped((f) => !f)}>
      <Face face={face} />
      <span className="craft-card__hint">{flipped ? 'Tap to go back' : 'Tap to reveal'}</span>
    </button>
  );
}

function Face({ face }: { face: any }) {
  if (!face) return null;
  if (face.contentType === 'image' && face.image) {
    return <img className="craft-card__image" src={imgSrc(face.image)} alt="" loading="lazy" />;
  }
  return <RichText value={face.text} className="craft-card__text" />;
}

/* ───────────────────────────── process ───────────────────────────── */

function ProcessBlock({ block }: { block: CraftBlock }) {
  const p = block.process ?? {};
  const steps: any[] = p.steps ?? [];
  return (
    <section className="craft-process">
      {p.introduction?.isVisible !== false && (
        <header>
          <RichText value={p.introduction?.title} className="craft-subheading" />
          <RichText value={p.introduction?.description} />
        </header>
      )}
      <ol>
        {steps.map((st, i) => (
          <li key={st.id ?? i}>
            <span className="craft-process__num">{i + 1}</span>
            <div>
              <RichText value={st.title} className="craft-process__title" />
              <RichText value={st.description} />
            </div>
          </li>
        ))}
      </ol>
      {p.summary?.isVisible !== false && (p.summary?.title || p.summary?.description) && (
        <footer className="craft-callout">
          <RichText value={p.summary?.title} className="craft-process__title" />
          <RichText value={p.summary?.description} />
        </footer>
      )}
    </section>
  );
}

/* ───────────────────────────── tabs & accordion ───────────────────────────── */

function TabsBlock({ block }: { block: CraftBlock }) {
  const items: any[] = block.tabs?.tabs ?? [];
  const [open, setOpen] = useState(0);
  if (!items.length) return null;

  if (block.variant === 'accordion') {
    return (
      <div className="craft-accordion">
        {items.map((t, i) => (
          <div key={t.id ?? i} className={open === i ? 'is-open' : ''}>
            <button type="button" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}>
              <span>{t.title}</span><span aria-hidden>{open === i ? '−' : '+'}</span>
            </button>
            {open === i && (
              <div className="craft-accordion__body">
                {imgSrc(t.media) ? <img src={imgSrc(t.media)} alt="" loading="lazy" /> : null}
                <RichText value={t.content} />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="craft-tabs">
      <div className="craft-tabs__strip" role="tablist">
        {items.map((t, i) => (
          <button key={t.id ?? i} type="button" role="tab" aria-selected={open === i}
            className={open === i ? 'is-active' : ''} onClick={() => setOpen(i)}>
            {t.title}
          </button>
        ))}
      </div>
      <div className="craft-tabs__panel" role="tabpanel">
        {imgSrc(items[open]?.media) ? <img src={imgSrc(items[open].media)} alt="" loading="lazy" /> : null}
        <RichText value={items[open]?.content} />
      </div>
    </div>
  );
}

/* ───────────────────────────── in-lesson questions ───────────────────────────── */

/**
 * A check-your-understanding question. These are practice only: they are part
 * of the lesson content, are never recorded, and never affect a score.
 */
function QuestionBlock({ block }: { block: CraftBlock }) {
  return (
    <div className="craft-quiz">
      {(block.questions ?? []).map((q: any, i: number) => <CraftQuestion key={q.id ?? i} q={q} />)}
    </div>
  );
}

function CraftQuestion({ q }: { q: any }) {
  const [picked, setPicked] = useState<string | null>(null);
  const correctIds: string[] = (q.correctAnswers ?? []).map((a: any) => (typeof a === 'string' ? a : a.id));
  const isCorrect = picked != null && correctIds.includes(picked);

  return (
    <div className="craft-quiz__q">
      <RichText value={q.title} className="craft-quiz__title" />
      <ul>
        {(q.answers ?? []).map((a: any, i: number) => {
          const id = a.id ?? String(i);
          const chosen = picked === id;
          const state = picked == null ? '' : correctIds.includes(id) ? 'is-right' : chosen ? 'is-wrong' : '';
          return (
            <li key={id}>
              <button type="button" className={`craft-quiz__a ${state} ${chosen ? 'is-chosen' : ''}`}
                disabled={picked != null} onClick={() => setPicked(id)}>
                <RichText value={a.title ?? a} />
              </button>
            </li>
          );
        })}
      </ul>
      {picked != null && (
        <p className={`craft-quiz__verdict ${isCorrect ? 'is-right' : 'is-wrong'}`}>
          {isCorrect ? 'Correct' : 'Not quite'} — practice only, this is not part of your score.
        </p>
      )}
    </div>
  );
}

/* ───────────────────────────── labeled graphic & media ───────────────────────────── */

function LabeledGraphic({ block }: { block: CraftBlock }) {
  const img = (block.images ?? [])[0];
  const labels: any[] = block.graphicLabels ?? [];
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="craft-labeled">
      <div className="craft-labeled__stage">
        {img ? <img src={imgSrc(img)} alt="" loading="lazy" /> : null}
        {labels.map((l, i) => (
          <button key={l.id ?? i} type="button" className={`craft-pin ${open === i ? 'is-open' : ''}`}
            style={{ left: `${l.xOffsetPct}%`, top: `${l.yOffsetPct}%` }}
            aria-label={plainText(l.title) || `Point ${i + 1}`}
            onClick={() => setOpen(open === i ? null : i)}>
            {i + 1}
          </button>
        ))}
      </div>
      {open != null && (
        <div className="craft-labeled__note">
          <RichText value={labels[open].title} className="craft-process__title" />
          <RichText value={labels[open].body} />
        </div>
      )}
    </div>
  );
}

function MultimediaBlock({ block, downloadDisabled }: { block: CraftBlock; downloadDisabled: boolean }) {
  return (
    <div className="craft-media">
      {(block.media ?? []).map((m: any, i: number) => {
        const v = m.video;
        if (!v) return null;
        const src = v.urls?.original ?? v.urls?.embed;
        if (!src) return null;
        if (v.source === 'youtube' || /youtube|vimeo/.test(src)) {
          return <iframe key={i} src={src} title={v.title ?? 'Video'} allowFullScreen loading="lazy"
            allow="accelerometer; encrypted-media; picture-in-picture" />;
        }
        return <video key={i} src={src} controls controlsList={downloadDisabled ? 'nodownload' : undefined} preload="metadata" />;
      })}
    </div>
  );
}
